'use client';

import { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ImageUploadOverlay from './image-upload-overlay';
import { compressImage } from '../../utils/compress-image';

type ImageDropzoneProps = {
  previewUrl: string | null;
  onFileAccepted: (file: File) => void;
  onRemove: () => void;
  disabled?: boolean;
};

function formatRejectionError(rejection: FileRejection): string {
  const errors = rejection.errors.map((e) => e.message).join(', ');
  return `${rejection.file.name}: ${errors}`;
}

const ImageDropzone = ({
  previewUrl,
  onFileAccepted,
  onRemove,
  disabled,
}: ImageDropzoneProps) => {
  const [rejectionErrors, setRejectionErrors] = useState<string[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [errorUrl, setErrorUrl] = useState<string | null>(null);
  const imageError = previewUrl !== null && previewUrl === errorUrl;

  const onDrop = useCallback(
    async (accepted: File[], rejections: FileRejection[]) => {
      setRejectionErrors(rejections.map(formatRejectionError));
      if (accepted.length > 0) {
        setCompressing(true);
        const compressed = await compressImage(accepted[0]);
        setCompressing(false);
        onFileAccepted(compressed);
      }
    },
    [onFileAccepted],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxSize: 50_000_000,
    multiple: false,
    disabled: disabled || compressing,
  });

  return (
    <div>
      <Box
        {...getRootProps()}
        sx={{
          display: previewUrl ? 'inline-block' : 'block',
          position: 'relative',
          border: previewUrl ? 'none' : '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'transparent',
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color 0.2s, background-color 0.2s',
          overflow: 'hidden',
          '&:hover .image-upload-overlay': {
            opacity: 1,
          },
        }}
      >
        <input {...getInputProps()} />

        {compressing ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={48} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Compressing image…
            </Typography>
          </Box>
        ) : previewUrl && !imageError ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={previewUrl}
              alt="Event image preview"
              onError={() => setErrorUrl(previewUrl)}
              sx={{
                display: 'block',
                maxWidth: 300,
                maxHeight: 300,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
            <ImageUploadOverlay onRemove={onRemove} disabled={disabled} />
          </Box>
        ) : previewUrl && imageError ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 200,
                height: 150,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <BrokenImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Image not available
              </Typography>
            </Box>
            <ImageUploadOverlay onRemove={onRemove} disabled={disabled} />
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AddPhotoAlternateIcon
              sx={{ fontSize: 48, color: 'grey.500', mb: 1 }}
            />
            <Typography variant="body1" color="text.secondary">
              Add an image
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Drag and drop or click to browse
            </Typography>
          </Box>
        )}
      </Box>

      {rejectionErrors.length > 0 && (
        <Alert
          severity="error"
          onClose={() => setRejectionErrors([])}
          sx={{ mt: 1 }}
        >
          {rejectionErrors.map((msg) => (
            <div key={msg}>{msg}</div>
          ))}
        </Alert>
      )}
    </div>
  );
};

export default ImageDropzone;
